import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  Link,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { authApi } from "../apis/authApi";

interface SuperOpsSetupFormProps {
  onBack: () => void;
  onSuccess?: () => void;
  onCredentialsSaved?: (platformName: string) => void;
}

const SuperOpsSetupForm: React.FC<SuperOpsSetupFormProps> = ({ onBack, onSuccess, onCredentialsSaved }) => {
  const [tokenKey, setTokenKey] = useState("");
  const [subdomain, setSubdomain] = useState("");
  const [graphqlUrl, setGraphqlUrl] = useState("https://api.superops.ai/msp");
  const [isTesting, setIsTesting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  
  // Track the values when test was successful
  const testedValuesRef = useRef<{
    tokenKey: string;
    subdomain: string;
    graphqlUrl: string;
  } | null>(null);
  
  // Check if fields have changed since successful test
  const hasFieldsChanged = () => {
    if (!testedValuesRef.current || !testResult?.success) {
      return true;
    }
    const current = testedValuesRef.current;
    return (
      tokenKey !== current.tokenKey ||
      subdomain !== current.subdomain ||
      graphqlUrl !== current.graphqlUrl
    );
  };
  
  const isSaveDisabled = !testResult?.success || hasFieldsChanged() || isSaving;

  const handleTestConnection = async () => {
    if (!tokenKey || !subdomain) {
      setTestResult({
        success: false,
        message: "Please fill in Token Key and Subdomain before testing the connection.",
      });
      return;
    }

    setIsTesting(true);
    setTestResult(null);

    try {
      const result = await authApi.testSuperOpsConnection({
        token_key: tokenKey,
        subdomain,
        graphql_url: graphqlUrl,
      });

      if (result.success) {
        // Store the tested values
        testedValuesRef.current = {
          tokenKey,
          subdomain,
          graphqlUrl,
        };
      }

      setTestResult({
        success: result.success,
        message: result.message || "Connection test successful!",
      });
    } catch (error: any) {
      // Extract error message from response if available
      const errorMessage = error.response?.data?.error || error.message || "Connection test failed. Please check your credentials.";
      setTestResult({
        success: false,
        message: errorMessage,
      });
      testedValuesRef.current = null;
    } finally {
      setIsTesting(false);
    }
  };

  const handleSave = async () => {
    if (isSaveDisabled) {
      return;
    }

    setIsSaving(true);

    try {
      await authApi.saveSuperOpsConfiguration({
        token_key: tokenKey,
        subdomain,
        graphql_url: graphqlUrl,
      });

      // Show success message
      setShowSuccessSnackbar(true);
      
      if (onCredentialsSaved) {
        setTimeout(() => onCredentialsSaved("superops"), 800);
        return;
      }
      if (onSuccess) {
        setTimeout(() => onSuccess(), 1500);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || "Failed to save SuperOps configuration.";
      setTestResult({
        success: false,
        message: errorMessage,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        sx={{
          fontFamily: "var(--theo-font-sans)",
          color: "#6c6c6c",
          textTransform: "none",
          mb: 3,
          "&:hover": {
            background: "#f5f5f5",
          },
        }}
      >
        Back to PSA Selection
      </Button>

      <Typography
        variant="h5"
        component="h2"
        sx={{
          fontFamily: "var(--theo-font-sans)",
          fontWeight: 700,
          mb: 3,
          color: "#1a1a1a",
        }}
      >
        SuperOps Setup
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          background: "#f8fafc",
          borderRadius: 2,
          border: "1px solid #e5e5e5",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: "var(--theo-font-sans)",
            fontWeight: 600,
            mb: 2,
            color: "#1a1a1a",
          }}
        >
          How to Get SuperOps API Credentials
        </Typography>
        <Box
          component="ol"
          sx={{
            fontFamily: "var(--theo-font-sans)",
            color: "#6c6c6c",
            pl: 3,
            "& li": {
              mb: 1.5,
              lineHeight: 1.6,
            },
          }}
        >
          <li>
            Log in to your SuperOps account as an administrator.
          </li>
          <li>
            Navigate to <strong>Settings &gt; API &amp; Integrations</strong>.
          </li>
          <li>
            Copy your <strong>API Token Key</strong> and <strong>Subdomain</strong>.
          </li>
          <li>
            Save the credentials securely. You'll need them to complete this setup.
          </li>
          <li>
            For more detailed instructions, visit the{" "}
            <Link
              href="https://docs.superops.ai/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: "#705d4a" }}
            >
              SuperOps API documentation
            </Link>
            .
          </li>
        </Box>
      </Paper>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <TextField
          label="Token Key"
          type="password"
          value={tokenKey}
          onChange={(e) => {
            setTokenKey(e.target.value);
            if (testResult?.success) {
              setTestResult(null);
            }
          }}
          placeholder="api-..."
          fullWidth
          required
          sx={{
            fontFamily: "var(--theo-font-sans)",
            "& .MuiInputLabel-root": {
              fontFamily: "var(--theo-font-sans)",
            },
            "& .MuiInputBase-input": {
              fontFamily: "var(--theo-font-sans)",
            },
          }}
          helperText="Your SuperOps API Token Key"
        />

        <TextField
          label="Subdomain"
          value={subdomain}
          onChange={(e) => {
            setSubdomain(e.target.value);
            if (testResult?.success) {
              setTestResult(null);
            }
          }}
          placeholder="your-subdomain"
          fullWidth
          required
          sx={{
            fontFamily: "var(--theo-font-sans)",
            "& .MuiInputLabel-root": {
              fontFamily: "var(--theo-font-sans)",
            },
            "& .MuiInputBase-input": {
              fontFamily: "var(--theo-font-sans)",
            },
          }}
          helperText="Your SuperOps subdomain (e.g., asyncmsp)"
        />

        <TextField
          label="GraphQL URL"
          value={graphqlUrl}
          onChange={(e) => {
            setGraphqlUrl(e.target.value);
            if (testResult?.success) {
              setTestResult(null);
            }
          }}
          placeholder="https://api.superops.ai/msp"
          fullWidth
          sx={{
            fontFamily: "var(--theo-font-sans)",
            "& .MuiInputLabel-root": {
              fontFamily: "var(--theo-font-sans)",
            },
            "& .MuiInputBase-input": {
              fontFamily: "var(--theo-font-sans)",
            },
          }}
          helperText="SuperOps GraphQL API endpoint (default: https://api.superops.ai/msp)"
        />

        {testResult && (
          <Alert
            severity={testResult.success ? "success" : "error"}
            sx={{
              fontFamily: "var(--theo-font-sans)",
              "& .MuiAlert-message": {
                fontFamily: "var(--theo-font-sans)",
              },
            }}
          >
            {testResult.message}
          </Alert>
        )}

        <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
          <Button
            variant="outlined"
            onClick={handleTestConnection}
            disabled={isTesting || !tokenKey || !subdomain}
            sx={{
              fontFamily: "var(--theo-font-sans)",
              textTransform: "none",
              fontWeight: 500,
              borderColor: "#705d4a",
              color: "#705d4a",
              "&:hover": {
                borderColor: "#604d3a",
                background: "#f5f5f5",
              },
              minWidth: 150,
            }}
          >
            {isTesting ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Testing...
              </>
            ) : (
              "Test Connection"
            )}
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaveDisabled}
            sx={{
              fontFamily: "var(--theo-font-sans)",
              textTransform: "none",
              fontWeight: 500,
              background: isSaveDisabled ? "#e0e0e0" : "#705d4a",
              color: isSaveDisabled ? "#9e9e9e" : "#fff",
              minWidth: 150,
              "&:hover": {
                background: isSaveDisabled ? "#e0e0e0" : "#604d3a",
              },
            }}
          >
            {isSaving ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowSuccessSnackbar(false)}
          severity="success"
          sx={{ fontFamily: "var(--theo-font-sans)" }}
        >
          SuperOps has been successfully connected!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SuperOpsSetupForm;
